import { queryOptions, useQuery } from "@tanstack/react-query";
import {
  featureFlagsGet,
  getFeatureFlagsGetQueryKey,
} from "@/services/api/feature-flags/feature-flags";
import { queryClient } from "@/services/queryClient";
import { useAuthStore } from "@/stores/authStore";
import type { FeatureFlagsResponse } from "@/types/api";

/** Every flag reads as off until the API answers, or if it can't (fail closed, matching the API). */
const FLAGS_OFF: FeatureFlagsResponse = { payments: false };

/**
 * PostHog flags are evaluated by the API per caller (no analytics consent or posthog-js needed), so the cache is
 * keyed by the signed-in user and re-fetched when they log in or out.
 */
export const featureFlagsQueryOptions = (userId: string | undefined) =>
  queryOptions({
    queryKey: [...getFeatureFlagsGetQueryKey(), userId ?? "anonymous"],
    queryFn: ({ signal }) => featureFlagsGet(signal),
    staleTime: 60_000,
  });

/** Resolves the current caller's flags for route guards (`beforeLoad`); failures resolve to all-off. */
export async function fetchFeatureFlags(): Promise<FeatureFlagsResponse> {
  try {
    return await queryClient.ensureQueryData(
      featureFlagsQueryOptions(useAuthStore.getState().user?.id),
    );
  } catch {
    return FLAGS_OFF;
  }
}

export function useFeatureFlags(): FeatureFlagsResponse {
  const userId = useAuthStore((state) => state.user?.id);
  const { data } = useQuery(featureFlagsQueryOptions(userId));
  return data ?? FLAGS_OFF;
}

/** Paid courses, Stripe checkout, discount codes, purchases, admin pricing and sales reporting. */
export const usePaymentsEnabled = () => useFeatureFlags().payments;
